import { useCallback, useEffect, useRef } from "react";
import {
    EMAIL_MERGE_TAG_CLASS,
    clearEmailMergeTagSelection,
    decorateEmailMergeTagsInHtml,
    formatSelectedEmailMergeTag,
    getSelectedEmailMergeTagElement,
    getSelectedEmailMergeTagPlainText,
    getPlainTextOffsetBeforeCaret,
    insertTextAtContentEditableCaret,
    placeCaretAfterMergeTag,
    placeCaretAtPlainTextOffset,
    selectEmailMergeTagElement,
    serializeEmailMergeTagsFromEditor,
} from "../utils/emailMergeVariables";

/**
 * contentEditable helpers: inline {{parameterN}} widgets with per-tag remove (×).
 * React state stores plain HTML; the DOM shows decorated widgets.
 *
 * Pass decorateHtml/serializeHtml for Raw HTML source mode (escaped markup + widgets).
 * serializeHtml(innerHTML, editorEl) — editorEl lets Raw mode walk the live DOM.
 */
const useEmailMergeEditor = ({
    editorRef,
    html,
    setHtml,
    onAfterChange,
    enabled = true,
    decorateHtml = decorateEmailMergeTagsInHtml,
    serializeHtml = serializeEmailMergeTagsFromEditor,
    onMergeTagRenameRequest,
}) => {
    const editorOwnsStateRef = useRef(false);

    const readPlainFromEditor = useCallback(() => {
        const el = editorRef.current;
        if (!el) return "";
        return serializeHtml(el.innerHTML || "", el);
    }, [editorRef, serializeHtml]);

    const writeDecoratedToEditor = useCallback(
        (plain) => {
            if (!editorRef.current) return;
            editorRef.current.innerHTML = decorateHtml(plain || "");
        },
        [editorRef, decorateHtml]
    );

    const commitPlainFromEditor = useCallback(
        (plain) => {
            editorOwnsStateRef.current = true;
            setHtml(plain);
            onAfterChange?.(plain);
        },
        [setHtml, onAfterChange]
    );

    const syncPlainStateFromEditor = useCallback(() => {
        const plain = readPlainFromEditor();
        commitPlainFromEditor(plain);
        return plain;
    }, [readPlainFromEditor, commitPlainFromEditor]);

    const refreshEditorDecoration = useCallback(() => {
        const plain = readPlainFromEditor();
        writeDecoratedToEditor(plain);
        commitPlainFromEditor(plain);
        return plain;
    }, [readPlainFromEditor, writeDecoratedToEditor, commitPlainFromEditor]);

    const applyPlainHtmlToEditor = useCallback(
        (plain, { notify = true } = {}) => {
            setHtml(plain || "");
            writeDecoratedToEditor(plain || "");
            if (notify) {
                onAfterChange?.(plain || "");
            }
        },
        [setHtml, writeDecoratedToEditor, onAfterChange]
    );

    useEffect(() => {
        if (!enabled || !editorRef.current) return;

        if (editorOwnsStateRef.current) {
            editorOwnsStateRef.current = false;
            return;
        }

        const plainInEditor = serializeHtml(
            editorRef.current.innerHTML || "",
            editorRef.current
        );
        const plainState = html || "";

        if (plainInEditor !== plainState) {
            writeDecoratedToEditor(plainState);
        }
    }, [enabled, html, editorRef, writeDecoratedToEditor, serializeHtml]);

    const handleEditorInput = useCallback(() => {
        syncPlainStateFromEditor();
    }, [syncPlainStateFromEditor]);

    const handleEditorBlur = useCallback(() => {
        const plain = readPlainFromEditor();
        // No content change — do not rewrite or notify (avoids false "edited" state).
        if (plain === (html || "")) {
            return;
        }
        // Skip DOM rewrite when plain text is unchanged so saved caret ranges
        // stay valid while insert/rename dialogs are open.
        writeDecoratedToEditor(plain);
        commitPlainFromEditor(plain);
    }, [readPlainFromEditor, writeDecoratedToEditor, commitPlainFromEditor, html]);

    const handleEditorMouseDown = useCallback(
        (event) => {
            const editor = editorRef.current;
            if (!editor) return;

            const removeBtn = event.target.closest?.(".email-merge-tag__remove");
            if (removeBtn && editor.contains(removeBtn)) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            const tag = event.target.closest?.(`.${EMAIL_MERGE_TAG_CLASS}`);
            if (tag && editor.contains(tag)) {
                // Click chip body → select (like × removes). Keep selection for format/copy.
                event.preventDefault();
                selectEmailMergeTagElement(tag, editor);
            } else {
                clearEmailMergeTagSelection(editor);
            }
        },
        [editorRef]
    );

    const handleEditorClick = useCallback(
        (event) => {
            const editor = editorRef.current;
            if (!editor) return;

            const removeBtn = event.target.closest?.(".email-merge-tag__remove");
            if (removeBtn && editor.contains(removeBtn)) {
                event.preventDefault();
                event.stopPropagation();

                removeBtn.closest(`.${EMAIL_MERGE_TAG_CLASS}`)?.remove();

                const plain = readPlainFromEditor();
                commitPlainFromEditor(plain);
                return;
            }

            const tag = event.target.closest?.(`.${EMAIL_MERGE_TAG_CLASS}`);
            if (tag && editor.contains(tag)) {
                event.preventDefault();
                selectEmailMergeTagElement(tag, editor);
            }
        },
        [editorRef, readPlainFromEditor, commitPlainFromEditor]
    );

    const handleEditorCopy = useCallback(
        (event) => {
            const plainTag = getSelectedEmailMergeTagPlainText(editorRef.current);
            if (!plainTag) return;

            event.preventDefault();
            event.clipboardData?.setData("text/plain", plainTag);
        },
        [editorRef]
    );

    const handleEditorCut = useCallback(
        (event) => {
            const editor = editorRef.current;
            const tag = getSelectedEmailMergeTagElement(editor);
            const plainTag = getSelectedEmailMergeTagPlainText(editor);
            if (!tag || !plainTag) return;

            event.preventDefault();
            event.clipboardData?.setData("text/plain", plainTag);
            tag.remove();
            commitPlainFromEditor(readPlainFromEditor());
        },
        [editorRef, readPlainFromEditor, commitPlainFromEditor]
    );

    /**
     * Apply a toolbar command. Merge chips use wrap/unwrap; other text uses execCommand.
     * @param {string} command
     * @param {string|null} [value]
     * @returns {boolean}
     */
    const runEditorCommand = useCallback(
        (command, value = null) => {
            const editor = editorRef.current;
            if (!editor) return false;

            editor.focus();

            const selectedTag = getSelectedEmailMergeTagElement(editor);
            if (selectedTag && formatSelectedEmailMergeTag(selectedTag, command)) {
                selectEmailMergeTagElement(selectedTag, editor);
                syncPlainStateFromEditor();
                return true;
            }

            document.execCommand(command, false, value);
            syncPlainStateFromEditor();
            return true;
        },
        [editorRef, syncPlainStateFromEditor]
    );

    const handleEditorContextMenu = useCallback(
        (event) => {
            const editor = editorRef.current;
            if (!editor || typeof onMergeTagRenameRequest !== "function") return;

            const tag = event.target.closest?.(`.${EMAIL_MERGE_TAG_CLASS}`);
            if (!tag || !editor.contains(tag)) return;

            event.preventDefault();
            event.stopPropagation();

            selectEmailMergeTagElement(tag, editor);
            const name = tag.getAttribute("data-merge");
            if (name) {
                onMergeTagRenameRequest(name);
            }
        },
        [editorRef, onMergeTagRenameRequest]
    );

    const insertMergeTagInEditor = useCallback(
        (name, caret) => {
            const editor = editorRef.current;
            if (!editor || !name) return;

            const tag = `{{${name}}}`;
            const offsetBefore = getPlainTextOffsetBeforeCaret(
                editor,
                caret,
                serializeHtml
            );
            const caretAfterInsert = offsetBefore + tag.length;

            insertTextAtContentEditableCaret(editor, tag, caret);
            refreshEditorDecoration();

            const restoreCaret = () => {
                const el = editorRef.current;
                if (!el) return;
                if (!placeCaretAtPlainTextOffset(el, caretAfterInsert)) {
                    placeCaretAfterMergeTag(el, name, { preferLast: true });
                }
            };

            restoreCaret();
            requestAnimationFrame(restoreCaret);
        },
        [editorRef, refreshEditorDecoration, serializeHtml]
    );

    return {
        readPlainFromEditor,
        applyPlainHtmlToEditor,
        refreshEditorDecoration,
        syncPlainStateFromEditor,
        runEditorCommand,
        handleEditorInput,
        handleEditorBlur,
        handleEditorMouseDown,
        handleEditorClick,
        handleEditorCopy,
        handleEditorCut,
        handleEditorContextMenu,
        insertMergeTagInEditor,
    };
};

export default useEmailMergeEditor;
